/**
 * PatientIntakeSection — the first thing a rep sees on the Profile tab.
 *
 * Card CONTACT-PROFILE-INTAKE items 7, 8 and 9 (Carlos, CRM 2.0 meeting
 * 9 Oct 2026; first-wave work ahead of the 14 Oct review).
 *
 *   item 7  basic patient fields, then the Discovery Call questions, at the TOP
 *   item 8  keep autosave, add a Save button, a visible confirmation, a visible
 *           error naming the field
 *   item 9  required fields highlighted and counted, never blocking
 *
 * ── WHAT "SAME SAVE PATH, MOVED NOT COPIED" MEANT HERE ────────────────────
 *
 * The basic patient fields were never on the Profile tab. They were in the
 * right-hand "Contact Info" card behind an Edit → Save flow that saved through
 * `crm:contacts:update`. The FIELDS moved here and the side panel keeps the
 * read-only view. The CHANNEL did not come with them, for the reason below —
 * `crm:contacts:update` is untouched and still serves `EditContactModal`.
 *
 * ⛔⛔ AND THAT OLD CALLER COULD NEVER HAVE SUCCEEDED. Measured at d0cc1558:
 * ContactDetailView's `startEditing` built `editForm` in snake_case
 * (`first_name`, `last_name`, `contact_type`) and `crm:contacts:update` reads
 * camelCase (`data.firstName`, `data.lastName`, `data.contactType`). All three
 * arrived `undefined`, all three are NOT NULL on `crm_contacts`, so every save
 * from that card died on a constraint and alerted "Save failed". Carlos asked
 * for a confirmation after a successful save; there were no successful saves to
 * confirm.
 *
 * ⚠⚠ THE NULL NAMES WERE THE ONLY THING SAVING THE REST OF THE ROW. That
 * handler is a FULL-ROW UPDATE of 22 columns. `editForm` carried 9 keys, so
 * `title, fax, address, zip, referral_source, pipeline_stage, assigned_to,
 * tags, dob, lead_source, medical_interest, next_follow_up_date` were all
 * absent and would have been written NULL. The NOT NULL violation aborted the
 * statement before the wipe could commit. Fix the names alone and the first
 * successful save erases a patient's date of birth.
 *
 * ⛔⛔ AND THE FIRST FIX FOR THAT WAS ALSO WRONG — PR #618 review round 1.
 *
 * The obvious repair is to send a COMPLETE camelCase payload, seeded from the
 * loaded contact, so the full-row UPDATE is a no-op for untouched columns.
 * That is right for a MODAL that loads, edits and submits once — it is what
 * `EditContactModal` (CRMPage.js:201) has always done. It is WRONG for a panel
 * that AUTOSAVES, and the review caught it:
 *
 *   a. LOST UPDATES. Every keystroke rewrote pipeline_stage, assigned_to,
 *      tags, notes, dob, lead_source and next_follow_up_date from a snapshot
 *      taken when the screen loaded. A stage set by the pipeline board, a
 *      reassignment, a GHL import — reverted by a rep fixing a phone number,
 *      silently, with no way for either of them to notice.
 *   b. A NULL pipeline_stage (common on GHL imports) became 'new' on the first
 *      edit of ANY field, moving the contact into the pipeline.
 *   c. dob was re-encrypted on every save of every field.
 *
 * ⇒ This panel now sends ONLY the dirty fields, to `crm:contacts:patch-intake`
 * (routes/crm.js), which carries its own allowlist of exactly these columns,
 * refuses anything outside it, enforces the three NOT NULLs, and writes dob
 * through the same `_encDob` envelope as every other writer. Nothing this
 * panel does not render can move.
 *
 * ⚠ The full-row trap is kept under test in
 * `tests/integration/contactIntakeFullRowUpdate.test.js` — not because we use
 * that path, but because it documents why we must not.
 *
 * ── AUTOSAVE ──────────────────────────────────────────────────────────────
 *
 * The bookkeeping mirrors ContactFieldsPanel after #606, for the same reason:
 * a reload must never overwrite the box a human is typing into. Typed controls
 * debounce 700 ms and flush on blur — DATES INCLUDED, see the date control
 * for why. Only selects commit at once.
 *
 * ⛔ SYNTHETIC DATA ONLY in every test that touches this file.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { ClipboardList, AlertTriangle, Check, Loader2 } from 'lucide-react';
import { DiscoveryCaptureForm } from './DiscoveryCallPanel';
import {
  REQUIRED_INTAKE_FIELDS, missingRequiredFields, missingRequiredSummary,
} from '../lib/requiredIntakeFields';

/* ─── Contact type vocabulary ─────────────────────────────────────────────
   ⚠ Mirrors the CHECK constraint on crm_contacts.contact_type. A value not in
   this list is rejected by the database, so the control is a select and never
   a free-text box. */
const CONTACT_TYPE_OPTIONS = [
  ['referral_physician', 'Referral Physician'],
  ['insurance_rep', 'Insurance Rep'],
  ['vendor', 'Vendor'],
  ['prospect', 'Prospect'],
  ['partner', 'Partner'],
  ['other', 'Other'],
];

const REFERRAL_SOURCE_OPTIONS = [
  'physician_referral', 'self_referral', 'web', 'walk_in',
  'insurance', 'marketing', 'word_of_mouth', 'other',
];

/**
 * The fields this section renders, in the order a rep reads them out.
 * `kind` picks the control; `typed` picks debounce-vs-commit-at-once.
 */
const INTAKE_FIELDS = [
  { name: 'first_name',          label: 'First name',          kind: 'text',     typed: true },
  { name: 'last_name',           label: 'Last name',           kind: 'text',     typed: true },
  { name: 'contact_type',        label: 'Contact type',        kind: 'enum',     options: CONTACT_TYPE_OPTIONS },
  { name: 'dob',                 label: 'Date of birth',       kind: 'date' },
  { name: 'email',               label: 'Email',               kind: 'text',     typed: true, inputType: 'email' },
  { name: 'phone',               label: 'Phone',               kind: 'text',     typed: true },
  { name: 'organization',        label: 'Organization',        kind: 'text',     typed: true },
  { name: 'title',               label: 'Title',               kind: 'text',     typed: true },
  { name: 'city',                label: 'City',                kind: 'text',     typed: true },
  { name: 'state',               label: 'State',               kind: 'text',     typed: true },
  { name: 'zip',                 label: 'ZIP',                 kind: 'text',     typed: true },
  { name: 'referral_source',     label: 'Referral source',     kind: 'enum',
    options: REFERRAL_SOURCE_OPTIONS.map((v) => [v, v.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())]) },
  { name: 'lead_source',         label: 'Lead source',         kind: 'text',     typed: true },
  { name: 'medical_interest',    label: 'Medical interest',    kind: 'text',     typed: true },
  { name: 'next_follow_up_date', label: 'Next follow-up',      kind: 'datetime' },
  { name: 'notes',               label: 'Notes',               kind: 'textarea', typed: true, fullWidth: true },
];

const REQUIRED_NAMES = new Set(REQUIRED_INTAKE_FIELDS.map((f) => f.name));
/*
  ⚠ The client's own guard, mirroring the server's _CRM_INTAKE_PATCHABLE. It is
  NOT the gate — the handler refuses an unlisted column and that is what counts
  — but a panel that can only ever name its own fields makes the refusal a
  programming error rather than something a user can trip.
*/
const INTAKE_FIELD_NAMES = new Set(INTAKE_FIELDS.map((f) => f.name));
const TYPING_DEBOUNCE_MS = 700;

/**
 * `dob` is a DATE — a calendar day with no zone. Slicing the first 10
 * characters is correct for it and MUST NOT be given timezone treatment: a
 * birthday shifted by an offset is a different birthday.
 */
const fmtDate = (v) => (v ? String(v).slice(0, 10) : '');

/**
 * ⛔⛔ `next_follow_up_date` IS A TIMESTAMPTZ, AND THE OLD CODE SHOWED IT IN
 * UTC — review round 2, item 4.
 *
 * Postgres serialises it as a UTC ISO string. The old `String(v).slice(0, 16)`
 * chopped that to `2026-12-01T09:30` and handed it to a `datetime-local`
 * input, which means local time — so a follow-up set for 09:30 Eastern was
 * displayed as 09:30 UTC, and saving it back wrote 09:30 Eastern. For Carlos's
 * team that is a FOUR TO FIVE HOUR SHIFT, in the field that drives the Daily
 * Hit List's "due today / overdue" buckets, and it compounds on every save.
 *
 * ⇒ Convert to the viewer's local time for display, and send back an absolute
 * instant. `toISOString()` carries the zone explicitly (`Z`), so nothing
 * downstream has to guess what the wall-clock string meant.
 */
function toLocalDateTimeInput(v) {
  if (!v) return '';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`
       + `T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** The inverse: a `datetime-local` string is LOCAL wall time. */
function fromLocalDateTimeInput(sVal) {
  if (!sVal) return null;
  const d = new Date(sVal);          // parsed in the viewer's zone
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();            // an absolute instant, zone carried
}

/**
 * The patch body: ONLY the fields that changed, under their column names.
 *
 * ⛔⛔ THIS REPLACED A FULL-ROW BUILDER, AND THE REASON IS THE WHOLE REVIEW —
 * PR #618 round 1.
 *
 * The first cut sent all 22 columns `crm:contacts:update` writes, seeded from
 * the loaded contact. That is the correct shape for a MODAL that loads, edits
 * and submits once. It is the wrong shape for an AUTOSAVING panel, and the
 * difference is not stylistic:
 *
 *   a. LOST UPDATES. Every keystroke rewrote pipeline_stage, assigned_to,
 *      tags, notes, dob, lead_source and next_follow_up_date from a snapshot
 *      taken when the screen loaded. A stage change made by the pipeline
 *      board, a reassignment, a GHL import — all silently reverted by a rep
 *      correcting a phone number, who would never know.
 *
 *   b. `pipelineStage: pick('pipeline_stage') || 'new'` MOVED a GHL-imported
 *      contact with no stage INTO the pipeline on the first edit of any field.
 *
 *   c. dob was re-encrypted on every save of every field.
 *
 * ⇒ Only the dirty fields are sent, to `crm:contacts:patch-intake`, which
 * writes only the columns named. Nothing the panel does not render can move.
 *
 * @param {Record<string, unknown>} local  the local copy (server column names)
 * @param {string[]} fields                the columns to send
 */
export function buildIntakePatch(local, fields) {
  const out = {};
  for (const name of (fields || [])) {
    if (!INTAKE_FIELD_NAMES.has(name)) continue;   // never send what we do not own
    let v = local ? local[name] : undefined;
    /*
      ⚠ `dob` is normalised to a calendar day; `next_follow_up_date` is already
      an absolute ISO instant by the time it reaches here (the control converts
      on change), so it is sent AS IS. Re-slicing it was how the zone got
      dropped on the way out.
    */
    if (name === 'dob') v = fmtDate(v);
    out[name] = v === undefined ? null : v;
  }
  return out;
}

/** HH:MM in the viewer's locale — the time the SERVER confirmed, not the click. */
function clockTime(d) {
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

const labelStyle = {
  fontSize: 10, fontWeight: 700, color: '#475569',
  textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4,
};
const controlStyle = {
  width: '100%', padding: '6px 10px', borderRadius: 6,
  border: '1px solid #CBD5E1', fontSize: 13, color: '#0F172A',
  background: '#FFFFFF', outline: 'none', boxSizing: 'border-box',
};
/* ⚠ The highlight is a BORDER AND A WORD, never colour alone — a red outline
   is invisible to a red-green colourblind rep, and this is a clinical form. */
const missingControlStyle = {
  ...controlStyle, border: '2px solid #DC2626', background: '#FEF2F2',
};

export default function PatientIntakeSection({ contact, mountContactId, onPatched, canUpdate = true }) {
  /*
    ⛔⛔ THE CONTACT THIS INSTANCE IS FOR, PINNED AT MOUNT — review round 4,
    blocking item (ii).

    The component is keyed by contact id, so a switch gives B a fresh instance.
    But the DATA under that key lags: ContactDetailView holds the previous
    patient's row until its load resolves, so B's instance could mount holding
    A's values and A's id. Keying fixed WHICH INSTANCE exists; it could not fix
    WHAT WAS IN IT.

    ⚠ `useRef` with no setter, read once: the pin cannot drift, by
    construction, which is the only reason it is worth more than another
    comparison. Every write snapshots THIS id — never `contactRef`, which is
    whatever the last render handed us.

    ⚠ ContactDetailView also refuses to render this panel until its data
    matches. This pin is the second line: it makes the component safe on its
    own, the way the enqueue snapshot does.
  */
  const pinnedIdRef = useRef(mountContactId ?? contact?.id ?? null);
  const pinnedId = pinnedIdRef.current;
  /** Does the row we were handed actually belong to the contact we are for? */
  /*
    ⚠ COMPARED AS STRINGS, like ContactDetailView's outer gate. A numeric id
    from the payload and a string id from a caller are the same record, and
    `===` would call them different — which fails SAFE (it refuses) but would
    lock the panel for a reason nobody could see. Consistency with the gate is
    what stops the two disagreeing about the same pair of ids.
  */
  const idMatches = String(contact?.id ?? '') === String(pinnedId ?? '');
  const contactId = contact?.id;

  const [local, setLocal] = useState(contact || {});
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [errors, setErrors] = useState([]);       // [{ label, detail }]
  const [noChanges, setNoChanges] = useState(false);

  /* The #606 bookkeeping. Each ref answers a different question; see
     ContactFieldsPanel for why none of them can be collapsed into another. */
  const dirtyRef = useRef({});
  const focusRef = useRef(null);
  const inFlightRef = useRef(0);
  const timersRef = useRef({});
  /*
    ⛔⛔ ORDERING, ADDED IN REVIEW ROUND 2. Two different races, two refs.

    `seqRef` — a monotonic per-field edit counter. Every commit remembers the
    value it was sent at, and a RESPONSE is only allowed to change anything if
    nothing newer has been typed since. Without it, a slow save that resolves
    after a faster later one clears `dirty` for a value that is no longer the
    one on screen, and the next reload overwrites the user's newer keystrokes.

    `chainRef` — a per-field promise chain. Commits for one field are
    SERIALIZED: the next never leaves until the previous has come back, so two
    writes for the same column cannot be in flight at once and cannot arrive at
    the database out of order. A counter alone does not give that — it can only
    decide what to do about a result that already landed, by which time the
    wrong value may already be stored.

    ⚠ A CHAIN PER FIELD, NOT ONE GLOBAL CHAIN. Different columns have no
    ordering relationship with each other, and a global chain would make a slow
    `notes` save hold up a `phone` save for no reason.
  */
  const seqRef = useRef({});
  const chainRef = useRef({});
  const localRef = useRef(local);
  useEffect(() => { localRef.current = local; }, [local]);
  const contactRef = useRef(contact);
  useEffect(() => { contactRef.current = contact; }, [contact]);

  /*
    ⚠ THERE IS NO "CONTACT CHANGED" PATH IN THIS COMPONENT ANY MORE — review
    round 3. It is KEYED BY CONTACT ID at its mount, so switching record
    unmounts this instance (flushing A's pending edits through A's own chain,
    with A's snapshot) and mounts a fresh one for B with empty refs.

    ⇒ Every "did the contact change under us" guard that used to live here is
    gone, because the state it guarded against can no longer be constructed.
    `tests/react/patientIntakeSection.test.js` asserts the remount behaviourally,
    so deleting the key breaks a test rather than quietly restoring the hazard.
  */

  const isProtected = useCallback((field) => (
    focusRef.current === field || !!dirtyRef.current[field] || inFlightRef.current > 0
  ), []);

  /*
    ⛔⛔ MERGE FIELD BY FIELD. NEVER `setLocal(contact)`. Identical reasoning to
    ContactFieldsPanel: this section stays mounted across the reload that each
    save triggers, so a wholesale replacement would reset the box under the
    rep's fingers mid-word.
  */
  useEffect(() => {
    if (!contact) return;
    setLocal((prev) => {
      /*
        ⚠ NO id-CHANGE BRANCH, AND THE REASON IS THE PIN ABOVE — not an
        assumption about the caller.

        The component is keyed by contact id AND refuses to render or write
        while `contact.id` differs from the id it was mounted for (`idMatches`).
        So by the time this merge runs, `prev` and `contact` describe the same
        record. A branch for "a different contact entirely" would be
        unreachable code wearing a guard's clothes; the guard is the pin.
      */
      const next = { ...prev };
      for (const key of Object.keys(contact)) {
        if (isProtected(key)) continue;
        next[key] = contact[key];
      }
      return next;
    });
  }, [contact, isProtected]);

  /** True while this instance is mounted; a reply after unmount touches no state. */
  const mountedRef = useRef(true);
  useEffect(() => () => { mountedRef.current = false; }, []);

  const labelFor = (n) => INTAKE_FIELDS.find((f) => f.name === n)?.label || n;

  /**
   * Perform ONE write from a SNAPSHOT. It reads no refs for its content.
   *
   * ⛔⛔ THE SNAPSHOT IS THE WHOLE FIX — review round 3, blocking.
   *
   * This used to read `contactRef.current` and `localRef.current` when it RAN,
   * not when it was QUEUED. Commits are chained, so a write queued while
   * contact A was open could execute after the rep had switched to B — at
   * which point it read B's id and B's values, and then PASSED its own
   * "is this still the same contact" guard, because that was captured at run
   * time too. The result was a spurious write to B, a "Saved ✓" on B, and an
   * onPatched reload of B, all triggered by an edit made to A.
   *
   * ⚠ A guard captured at the same moment as the thing it guards cannot
   * disagree with it. That is why adding more guards here was never going to
   * work and the shape had to change.
   *
   * ONE request carrying ONLY the named fields — never the whole row; see
   * buildIntakePatch for why that distinction is the card.
   *
   * ⛔ `setSavedAt` ONLY INSIDE THE SUCCESS BRANCH. Carlos's item 8 is "no
   * silent success: the confirmation appears only after the server says
   * success". A confirmation set beside the call is a confirmation of having
   * clicked, which is the thing the rep already knows.
   *
   * @param {{contactId:number, payload:object, fields:string[], seq:object}} snap
   * @param {boolean} silent  true for the unmount flush: no UI, log only.
   */
  const runWrite = useCallback(async (snap, silent = false) => {
    const api = window.zignagenixAPI;
    const named = snap.fields.map(labelFor);
    const show = (detail) => {
      if (silent || !mountedRef.current) {
        // ⚠ FIELD NAMES ONLY. A rejected flush must leave a trace, and the
        // values in it are a patient's own data.
        console.warn('[PatientIntakeSection] intake save failed', { fields: snap.fields, detail });
        return;
      }
      setErrors([{ label: named.join(', ') || 'Patient intake', detail }]);
    };

    if (!api || typeof api.patchIntakeFields !== 'function') {
      show('the intake save channel is unavailable');
      return false;
    }

    if (!silent && mountedRef.current) { setSaving(true); setErrors([]); }
    inFlightRef.current += 1;
    try {
      const r = await api.patchIntakeFields(snap.contactId, snap.payload);
      /*
        ⚠ THREE SHAPES, NOT TWO. secureHandler answers a refusal with
        { error: true, code, message } at HTTP 200; the handler answers success
        with { success: true }; a thrown network error lands in catch. An
        `if (r.error)` alone treats a missing body as success.
      */
      if (!r || r.error === true || r.success === false) {
        show((r && (r.message || r.code)) || 'the server did not confirm the save');
        return false;
      }
      if (silent || !mountedRef.current) return true;
      /*
        ⛔ CLEAR `dirty` ONLY FOR FIELDS NOT EDITED SINCE THIS WAS QUEUED. Same
        doctrine as ContactFieldsPanel after #606: clearing it for a field the
        user has typed in again hands the next reload a licence to overwrite
        the newer keystrokes.
      */
      for (const f of snap.fields) {
        if (focusRef.current === f) continue;
        if ((seqRef.current[f] || 0) !== snap.seq[f]) continue;   // superseded
        delete dirtyRef.current[f];
      }
      setSavedAt(new Date());
      if (onPatched) onPatched();
      return true;
    } catch (err) {
      show(err?.message || 'save failed');
      return false;
    } finally {
      inFlightRef.current = Math.max(0, inFlightRef.current - 1);
      if (inFlightRef.current === 0 && !silent && mountedRef.current) setSaving(false);
    }
  }, [onPatched]);

  /**
   * Build the snapshot NOW and put the write on every touched field's chain.
   *
   * ⚠ Gated on the SET of fields and becomes the new tail for each, so the Save
   * button still sends ONE batched all-or-nothing request rather than one per
   * dirty column, while any individual column's writes stay strictly ordered
   * and never overlap.
   *
   * ⚠ allSettled, not all: runWrite catches its own errors, but a rejected
   * prior must never strand every later edit to that column.
   */
  const queueWriteRef = useRef(null);
  const queueWrite = useCallback((fieldsWanted, { silent = false } = {}) => {
    const fields = (fieldsWanted || []).filter((f) => INTAKE_FIELD_NAMES.has(f));
    if (fields.length === 0) return Promise.resolve(true);

    /*
      ⛔ A MISMATCHED ROW IS NOT SAVEABLE. If `contact` is not the record this
      instance was mounted for, every value in `localRef` belongs to someone
      else and there is nothing here worth sending.
    */
    if (!idMatches) {
      console.warn('[PatientIntakeSection] refused a write: the row does not match the mount',
        { pinnedId, got: contactRef.current?.id ?? null, fields });
      return Promise.resolve(false);
    }

    const snap = {
      // ⚠ THE PINNED id, never contactRef — see the pin above.
      contactId: pinnedId,
      payload: buildIntakePatch(localRef.current, fields),
      fields,
      seq: Object.fromEntries(fields.map((f) => [f, seqRef.current[f] || 0])),
    };

    const prior = fields.map((f) => chainRef.current[f] || Promise.resolve());
    const run = () => runWrite(snap, silent);
    const next = Promise.allSettled(prior).then(run, run);
    for (const f of fields) chainRef.current[f] = next;
    return next;
  }, [runWrite, idMatches, pinnedId]);
  /*
    ⚠ The unmount cleanup has an EMPTY dependency array on purpose — it must
    run only on unmount, never on every re-render of queueWrite. So it reaches
    the current queueWrite through a ref rather than closing over a stale one.
  */
  queueWriteRef.current = queueWrite;

  /** Discrete controls — selects — commit at once. */
  const patch = useCallback((field, value) => {
    setNoChanges(false);
    setLocal((prev) => ({ ...prev, [field]: value }));
    dirtyRef.current[field] = true;
    seqRef.current[field] = (seqRef.current[field] || 0) + 1;
    localRef.current = { ...localRef.current, [field]: value };
    return queueWrite([field]);
  }, [queueWrite]);

  /** Typed controls — 700 ms of quiet, or a blur, whichever comes first. */
  const patchTyped = useCallback((field, value) => {
    setNoChanges(false);
    setLocal((prev) => ({ ...prev, [field]: value }));
    dirtyRef.current[field] = true;
    seqRef.current[field] = (seqRef.current[field] || 0) + 1;
    localRef.current = { ...localRef.current, [field]: value };
    if (timersRef.current[field]) clearTimeout(timersRef.current[field]);
    timersRef.current[field] = setTimeout(() => {
      delete timersRef.current[field];
      queueWrite([field]);
    }, TYPING_DEBOUNCE_MS);
  }, [queueWrite]);

  /** Blur: stop waiting and save now, if there is anything to save. */
  const flushField = useCallback((field) => {
    if (focusRef.current === field) focusRef.current = null;
    if (timersRef.current[field]) {
      clearTimeout(timersRef.current[field]);
      delete timersRef.current[field];
    }
    if (dirtyRef.current[field]) queueWrite([field]);
  }, [queueWrite]);

  /**
   * The Save button — item 8(a). Flushes pending debounce timers first.
   *
   * ⛔⛔ IT FLUSHES FIRST. A rep who types into the last box and reaches
   * straight for Save never blurs it, so the 700 ms timer is still pending and
   * the newest keystrokes exist only in this component. Saving without
   * cancelling those timers would send the row as it was BEFORE the last word
   * — and then the timer would fire afterwards and send it again.
   */
  const saveNow = useCallback(async () => {
    const pending = Object.keys(timersRef.current);
    for (const f of pending) {
      clearTimeout(timersRef.current[f]);
      delete timersRef.current[f];
    }
    const fields = Array.from(new Set([...pending, ...Object.keys(dirtyRef.current)]));
    /*
      ⚠ NOTHING DIRTY IS NOT A SAVE, AND MUST NOT SHOW "Saved ✓". The full-row
      version always had something to send, so this state did not exist. The
      two honest answers are "nothing changed" or silence — never a success
      tick for a write that never happened.
    */
    if (fields.length === 0) { setNoChanges(true); return; }
    setNoChanges(false);
    await queueWrite(fields);
  }, [queueWrite]);

  /*
    ⛔ ON UNMOUNT, SEND WHAT IS STILL PENDING — THROUGH THE CHAIN.

    A debounce window is a span in which a typed edit exists only in this
    component, so navigating away inside it would silently discard the rep's
    work. Because this component is KEYED BY CONTACT ID, this cleanup is also
    the contact-switch path: leaving A unmounts A's instance, and the snapshot
    queued here is A's id with A's values.

    ⛔⛔ AND IT GOES THROUGH queueWrite, NOT STRAIGHT AT THE API — review round
    3. Calling patchIntakeFields directly put this write in PARALLEL with any
    commit already in flight for the same field, so the older value could land
    last. The chain is the only thing that orders them, and a write that skips
    it is a write with no ordering at all.
  */
  useEffect(() => () => {
    const timers = timersRef.current;
    for (const f of Object.keys(timers)) { clearTimeout(timers[f]); delete timers[f]; }
    const fields = Object.keys(dirtyRef.current).filter((f) => INTAKE_FIELD_NAMES.has(f));
    if (fields.length === 0) return;
    // ⚠ `silent` — the component is going away, so a result has nothing on
    // screen to report to; failures are logged with field names only.
    Promise.resolve(queueWriteRef.current(fields, { silent: true }))
      .catch(() => console.warn('[PatientIntakeSection] unmount flush rejected', { fields }));
    //
    // ⚠ NO `eslint-disable` comment here, and the empty dependency array needs
    // none. `react-hooks/exhaustive-deps` is NOT a configured rule in this
    // project — only `rules-of-hooks` is — and disabling an UNDEFINED rule is
    // itself a build ERROR ("Definition for rule ... was not found"). The same
    // note sits at CRMPage.js:1237 and ReputationTab.js:46; I added one anyway
    // and `npm run build` was the only gate that caught it, exactly as those
    // comments warn. The effect reaches the current queueWrite through
    // queueWriteRef, so there is no stale closure for the rule to object to.
  }, []);

  const missing = useMemo(() => missingRequiredFields(local), [local]);
  const missingNames = useMemo(() => new Set(missing.map((f) => f.name)), [missing]);
  const summary = missingRequiredSummary(local);

  if (!contact) return null;

  /*
    ⛔⛔ NOTHING EDITABLE UNTIL THE ROW IS THIS CONTACT'S. Rendering the fields
    with another patient's values — even for the instant before the real row
    arrives — invites a rep to read them as this patient's, and to type into
    them. There is no safe way to show an editable form whose contents belong
    to someone else.
  */
  if (!idMatches) {
    return (
      <div
        data-testid="patient-intake-waiting"
        style={{
          background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 10,
          marginBottom: 14, padding: '18px 16px', color: '#64748B', fontSize: 13,
        }}
      >
        Loading patient intake…
      </div>
    );
  }

  const renderControl = (f) => {
    const isMissing = missingNames.has(f.name);
    const style = isMissing ? missingControlStyle : controlStyle;
    const aria = REQUIRED_NAMES.has(f.name) ? `${f.label} (required)` : f.label;
    const common = {
      'aria-label': aria,
      'aria-invalid': isMissing || undefined,
      disabled: !canUpdate,
      style,
    };
    const value = local[f.name];

    if (f.kind === 'enum') {
      const required = REQUIRED_NAMES.has(f.name);
      /*
        ⛔ A REQUIRED ENUM MUST NOT OFFER "— not set —" AS A CHOICE. The column
        is NOT NULL, so `crm:contacts:patch-intake` refuses a null for it; an
        option that can only ever produce an error is a trap, not a choice.

        ⚠ But a legacy row can ARRIVE with the value empty, and the control has
        to render that honestly rather than silently showing the first real
        option as though it were stored. So the placeholder appears only when
        the current value is already empty, and is `disabled` — visible as a
        state, unreachable as an action.
      */
      const showPlaceholder = !required || !value;
      return (
        <select {...common} value={value || ''}
          onChange={(e) => patch(f.name, e.target.value || (required ? value : null))}>
          {showPlaceholder && (
            <option value="" disabled={required}>
              {required ? '— required —' : '— not set —'}
            </option>
          )}
          {f.options.map(([v, lab]) => <option key={v} value={v}>{lab}</option>)}
        </select>
      );
    }
    if (f.kind === 'date' || f.kind === 'datetime') {
      /*
        ⛔⛔ A DATE INPUT IS A TYPED CONTROL. It was treated as a discrete one —
        `onChange` went straight to patch(), which commits immediately — and
        that is wrong in the way that loses data (review round 2, blocking).

        ⚠⚠ CHROMIUM FIRES onChange FOR EVERY VALID INTERMEDIATE VALUE while a
        year is typed. Entering 1961 produces 0001 → 0019 → 0196 → 1961: FOUR
        saves for one act of typing, three of them wrong, with no ordering
        between them. If they land out of order the database keeps the year
        0196 while the screen reads 1961, and the next reload shows the wrong
        date of birth. A date of birth is identity — a wrong one on a clinical
        record is a patient-matching failure, not a typo.

        ⇒ The same 700 ms debounce and blur-flush as every other typed control.

        ⛔ AND AN EMPTY VALUE DOES NOT SCHEDULE A SAVE AT ALL. Backspacing one
        part of a date makes the input report '' for as long as it is
        incomplete, which the old code sent as null — wiping the stored date
        mid-edit, before the user had finished retyping it. An empty date now
        clears ONLY on blur, which is the one moment the user has said they
        meant it.
      */
      const isDate = f.kind === 'date';
      const fmt = isDate ? fmtDate : toLocalDateTimeInput;
      return (
        <input {...common} type={isDate ? 'date' : 'datetime-local'}
          value={fmt(value)}
          onFocus={() => { focusRef.current = f.name; }}
          onBlur={() => flushField(f.name)}
          onChange={(e) => {
            const raw = e.target.value;
            const next = raw === '' ? null : (isDate ? raw : fromLocalDateTimeInput(raw));
            if (raw === '') {
              // Track it locally, mark it dirty, send NOTHING until blur.
              setNoChanges(false);
              setLocal((prev) => ({ ...prev, [f.name]: null }));
              localRef.current = { ...localRef.current, [f.name]: null };
              dirtyRef.current[f.name] = true;
              seqRef.current[f.name] = (seqRef.current[f.name] || 0) + 1;
              if (timersRef.current[f.name]) {
                clearTimeout(timersRef.current[f.name]);
                delete timersRef.current[f.name];
              }
              return;
            }
            patchTyped(f.name, next);
          }} />
      );
    }
    if (f.kind === 'textarea') {
      return (
        <textarea {...common} rows={2} value={value || ''}
          style={{ ...style, resize: 'vertical', fontFamily: 'inherit' }}
          onFocus={() => { focusRef.current = f.name; }}
          onBlur={() => flushField(f.name)}
          onChange={(e) => patchTyped(f.name, e.target.value)} />
      );
    }
    return (
      <input {...common} type={f.inputType || 'text'} value={value || ''}
        onFocus={() => { focusRef.current = f.name; }}
        onBlur={() => flushField(f.name)}
        onChange={(e) => patchTyped(f.name, e.target.value)} />
    );
  };

  return (
    <div
      data-testid="patient-intake-section"
      style={{
        background: '#FFFFFF', border: '1px solid #CBD5E1', borderRadius: 10,
        marginBottom: 14, overflow: 'hidden',
      }}
    >
      {/* ═══ Header: title, missing count, Save, confirmation ═══ */}
      <div style={{
        padding: '10px 14px', background: '#E0F2FE', borderBottom: '1px solid #2E8B9833',
        display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
      }}>
        <ClipboardList size={16} color="#0369A1" />
        <span style={{ fontSize: 13, fontWeight: 700, color: '#0369A1' }}>Patient intake</span>

        {/* item 9 — the count. Never a gate. */}
        {summary && (
          <span
            data-testid="intake-missing-count"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 4,
              background: '#FEE2E2', color: '#991B1B', border: '1px solid #DC2626',
              borderRadius: 999, padding: '2px 10px', fontSize: 11, fontWeight: 700,
            }}
          >
            <AlertTriangle size={11} /> {summary}
          </span>
        )}

        <div style={{ flex: 1 }} />

        {/* item 8(c) — the error, naming the field. */}
        {errors.length > 0 && (
          <span
            role="alert"
            data-testid="intake-save-error"
            style={{
              background: '#FEE2E2', color: '#991B1B', border: '1px solid #DC2626',
              borderRadius: 6, padding: '3px 10px', fontSize: 11, fontWeight: 600,
            }}
          >
            ✗ Could not save {errors.map((e) => e.label).join(', ')} — {errors[0].detail}
          </span>
        )}

        {/* ⚠ "nothing changed" is NOT a success tick. */}
        {errors.length === 0 && noChanges && (
          <span data-testid="intake-no-changes"
            style={{ color: '#64748B', fontSize: 11, fontWeight: 600 }}>
            No changes to save
          </span>
        )}

        {/* item 8(b) — the confirmation. Only ever set in the success branch. */}
        {errors.length === 0 && !noChanges && savedAt && (
          <span
            data-testid="intake-saved-confirmation"
            style={{ color: '#15803D', fontSize: 11, fontWeight: 700 }}
          >
            <Check size={11} style={{ verticalAlign: '-1px' }} /> Saved ✓ {clockTime(savedAt)}
          </span>
        )}

        {/* item 8(a) — the Save button. */}
        {canUpdate && (
          <button
            type="button"
            onClick={saveNow}
            disabled={saving}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              padding: '5px 14px', borderRadius: 6, border: 'none',
              background: saving ? '#94A3B8' : '#2E8B98', color: '#FFFFFF',
              fontSize: 12, fontWeight: 700, cursor: saving ? 'wait' : 'pointer',
            }}
          >
            {saving ? <><Loader2 size={12} /> Saving…</> : 'Save'}
          </button>
        )}
      </div>

      <div style={{ padding: '14px 16px' }}>
        {/* ═══ Basic patient fields ═══ */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px 16px' }}>
          {INTAKE_FIELDS.map((f) => (
            <div key={f.name} style={{ gridColumn: f.fullWidth ? '1 / -1' : 'auto' }}>
              <div style={labelStyle}>
                {f.label}
                {REQUIRED_NAMES.has(f.name) && (
                  <span style={{ color: '#DC2626', marginLeft: 4 }} aria-hidden="true">*</span>
                )}
              </div>
              {renderControl(f)}
              {missingNames.has(f.name) && (
                <div style={{ fontSize: 10, color: '#DC2626', fontWeight: 600, marginTop: 3 }}>
                  Required — not yet filled in
                </div>
              )}
            </div>
          ))}
        </div>

        {/* ═══ Discovery Call questions — the SAME form Care > Discovery renders ═══ */}
        <div style={{ marginTop: 18, paddingTop: 14, borderTop: '1px solid #E2E8F0' }}>
          <div style={{ fontSize: 12, color: '#64748B', marginBottom: 8 }}>
            These are the Care &gt; Discovery questions. Saving here writes the same
            discovery capture and shows on the Discovery Call screen.
          </div>
          <DiscoveryCaptureForm
            contactId={contactId}
            showGuide={false}
            showHistory={false}
            heading="Discovery Call questions"
            onSaved={onPatched}
          />
        </div>
      </div>
    </div>
  );
}
